import maplibregl from "maplibre-gl";
import type { MapOptions, LayerSpecification } from "maplibre-gl";
import type { LatLngLiteral } from "../../types/map";
import { centroid } from "@turf/centroid";
import type { Feature, Point, Polygon } from "geojson";
import { DEFAULT_LOCATION } from "../constants/map";

const apiKey = import.meta.env.FOOJI_AWS_MAP_API_KEY as string;
const mapStyle = "Monochrome";
const awsRegion = import.meta.env.FOOJI_AWS_MAP_REGION as string;
const styleUrl = `https://maps.geo.${awsRegion}.amazonaws.com/v2/styles/${mapStyle}/descriptor?key=${apiKey}`;

function createPinSVG(fillColor = "#60861f") {
  const svg = `
   <svg width="56" height="66" viewBox="0 0 56 66" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_d_407_181954)">
      <path d="M42.3669 10.6582C34.4326 2.72395 21.5686 2.72395 13.6343 10.6582C5.70002 18.5925 5.70002 31.4566 13.6343
        39.3908C14.3246 40.0811 15.1816 40.9288 16.2053 41.9341L24.3512 49.8578C25.3687 50.8418 28.0006 52.9998 28.0006
        52.9998C28.0006 52.9998 30.6327 50.8423 31.6503 49.8584C34.9073 46.7093 37.5559 44.1335 39.5973 42.1299L42.3669
        39.3908C50.3012 31.4566 50.3012 18.5925 42.3669 10.6582Z" fill="${fillColor}"/>
    </g>
    <circle cx="28" cy="25.6665" r="7" fill="white"/>
    <defs>
      <filter
        id="filter0_d_407_181954"
        x="2.82459"
        y="4.38642"
        width="52.984"
        height="60.6425"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"/>
        <feOffset dx="1.31599" dy="5.8539"/>
        <feGaussianBlur stdDeviation="3.0875"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.255 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_407_181954"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_407_181954" result="shape"/>
      </filter>
    </defs>
  </svg>
    `;
  const blob = new Blob([svg], { type: "image/svg+xml" });
  return URL.createObjectURL(blob);
}

async function loadImage(src: string, crossOrigin = false): Promise<HTMLImageElement> {
  const img = new Image();
  if (crossOrigin) {
    img.crossOrigin = "anonymous";
  }
  img.src = src;

  return new Promise((resolve, reject) => {
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
}

function createPointFeature(coords: LatLngLiteral, properties = {}): Feature<Point> {
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [coords.lng, coords.lat]
    },
    properties
  };
}

function createPolygonFeature(coordinates: number[][]): Feature<Polygon> {
  if (JSON.stringify(coordinates[0]) !== JSON.stringify(coordinates[coordinates.length - 1])) {
    coordinates.push(coordinates[0]);
  }

  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [coordinates]
    },
    properties: {}
  };
}

function addImageToMap(map: maplibregl.Map, imageId: string, img: HTMLImageElement) {
  if (!map.hasImage(imageId)) {
    map.addImage(imageId, img);
  }
}

function createGeoJSONSource(features: Feature[]) {
  return {
    type: "geojson" as const,
    data: {
      type: "FeatureCollection" as const,
      features
    }
  };
}

export const setupMap = (container: HTMLDivElement, overrideOptions: Partial<MapOptions> = {}): maplibregl.Map => {
  return new maplibregl.Map({
    container,
    style: styleUrl,
    center: [DEFAULT_LOCATION.lng, DEFAULT_LOCATION.lat],
    zoom: 11,
    interactive: false,
    attributionControl: false,
    ...overrideOptions
  });
};

export const resetAllMarkers = (map: maplibregl.Map) => {
  const layers = map.getStyle()?.layers || [];
  layers.forEach((layer: LayerSpecification) => {
    const isMarkerLayer =
      layer.id.startsWith("marker") ||
      layer.id.startsWith("progress-marker") ||
      layer.id.startsWith("delivery-zone");

    if (isMarkerLayer && map.getLayer(layer.id)) {
      map.removeLayer(layer.id);
    }
  });

  const sources = map.getStyle()?.sources || {};
  Object.keys(sources).forEach((sourceId) => {
    const isMarkerSource =
      sourceId.startsWith("marker") ||
      sourceId.startsWith("progress-marker") ||
      sourceId.startsWith("delivery-zone");

    if (isMarkerSource && map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
  });
};

export const updateMap = async (map: maplibregl.Map, markerCoordinates: LatLngLiteral) => {
  resetAllMarkers(map);

  if (markerCoordinates) {
    map.jumpTo({
      center: [markerCoordinates.lng, markerCoordinates.lat],
      zoom: 15
    });

    const pinUrl = createPinSVG("#8A54FF");
    const img = await loadImage(pinUrl);
    addImageToMap(map, "custom-pin", img);

    const feature = createPointFeature(markerCoordinates);
    map.addSource("marker-source", createGeoJSONSource([feature]));

    map.addLayer({
      id: "marker-layer",
      type: "symbol",
      source: "marker-source",
      layout: {
        "icon-image": "custom-pin",
        "icon-size": 1,
        "icon-anchor": "bottom"
      }
    });
  } else {
    map.jumpTo({ center: [DEFAULT_LOCATION.lng, DEFAULT_LOCATION.lat], zoom: 15 });
  }
};

export const updateMapWithProgressMarkers = async (
  map: maplibregl.Map,
  progressLocations: { icon: string, coords: LatLngLiteral }[],
  isMobile: boolean
) => {
  resetAllMarkers(map);

  if (progressLocations?.length > 0) {
    const bounds = new maplibregl.LngLatBounds();

    const features = progressLocations.map((location, index) => {
      bounds.extend([location.coords.lng, location.coords.lat]);
      return createPointFeature(location.coords, { iconId: `progress-icon-${index}` });
    });

    for (const [index, location] of progressLocations.entries()) {
      const iconId = `progress-icon-${index}`;
      const img = await loadImage(location.icon, true);
      addImageToMap(map, iconId, img);
    }

    map.addSource("progress-marker-source", createGeoJSONSource(features));

    map.addLayer({
      id: "progress-marker-layer",
      type: "symbol",
      source: "progress-marker-source",
      layout: {
        "icon-image": ["get", "iconId"],
        "icon-size": 1,
        "icon-anchor": "bottom"
      }
    });

    map.fitBounds(bounds, { animate: false, zoom: isMobile ? 13 : 15 });
  } else {
    map.jumpTo({ center: [DEFAULT_LOCATION.lng, DEFAULT_LOCATION.lat], zoom: 15 });
  }
};

export const updateMapWithDeliveryZones = async (map: maplibregl.Map, deliveryZones: Array<Array<number[]>>) => {
  resetAllMarkers(map);

  const bounds = new maplibregl.LngLatBounds();
  const deliveryZoneFeatures = deliveryZones.map(zone => createPolygonFeature(zone));

  map.addSource("delivery-zone-source", createGeoJSONSource(deliveryZoneFeatures));

  map.addLayer({
    id: "delivery-zones-fill",
    type: "fill",
    source: "delivery-zone-source",
    paint: {
      "fill-color": "#60861f",
      "fill-opacity": 0.5
    }
  });

  map.addLayer({
    id: "delivery-zones-outline",
    type: "line",
    source: "delivery-zone-source",
    paint: {
      "line-color": "#60861f",
      "line-width": 2
    }
  });

  const centroidFeatures = deliveryZoneFeatures.map(zoneFeature => centroid(zoneFeature));
  const pinId = "centroid-pin";

  const img = await loadImage(createPinSVG("#EA4335"));
  addImageToMap(map, pinId, img);

  map.addSource("delivery-zone-centroids", createGeoJSONSource(centroidFeatures));

  map.addLayer({
    id: "delivery-zones-centroids-layer",
    type: "symbol",
    source: "delivery-zone-centroids",
    layout: {
      "icon-image": pinId,
      "icon-size": 0.8,
      "icon-anchor": "bottom"
    }
  });

  deliveryZones.flat().forEach(([lng, lat]) => bounds.extend([lng, lat]));
  map.fitBounds(bounds, { padding: 50, animate: false });
};
